import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Button, Modal, Link, Grid, CircularProgress, Tooltip } from "@mui/material";
import axios from "axios";
import { FilterContext } from "../FilterContext/FilterContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCopy,
  faCalendar,
  faMoneyBill,
  faPhone,
  faEdit,
  faMapMarker,
  faTimes,
  faBook,
  faGraduationCap,
  faLocationDot,
  faChalkboardTeacher,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import Message from "../../../Dashboard/Components/Messages/Message";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FacebookSharpIcon from "@mui/icons-material/FacebookSharp";
import Snackbar from "@mui/material/Snackbar";
import { getCourseImageUrl, getCourseFileUrl } from '../../../../utils/imageUtils';
import {ReactComponent as Curve} from "../../../../assets/images/undercurve.tsx";

const levels = [
  {
    value: "primary-7-11",
    key: "Primary (7/11+)",
  },
  {
    value: "primary-5-11",
    key: "Primary (5-11 yrs)",
  },
  {
    value: "secondary-11-14",
    key: "Seconday (11-14 yrs)",
  },
  {
    value: "gcse-15-16",
    key: "GCSE (15-16 yrs)",
  },
  {
    value: "a-level-5-18",
    key: "A-level (15-18 yrs)",
  },
  {
    value: "degree-adults",
    key: "Degree/Adults",
  },
  {
    value: "other",
    key: "Other",
  },
];
// Helper function to strip HTML tags and decode HTML entities
const stripHtmlAndDecode = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Helper function to format time from ISO string
const formatTime = (timeString) => {
  if (!timeString) return '';
  try {
    const date = new Date(timeString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If it's already in HH:MM format, return as is
      if (/^\d{1,2}:\d{2}$/.test(timeString)) {
        return timeString;
      }
      return '';
    }
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  } catch (error) {
    return '';
  }
};

export const CourseCard = (props) => {
  const { idd, viewMode = 'list' } = props;
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [id, setId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const {
    searchName,
    searchLocation,
    paymentMethod,
    selectedFilters,
    subject,
    level,
  } = useContext(FilterContext);
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);

  let userId;
  try {
    const persistedData = localStorage.getItem("persist:root");
    const userData = JSON.parse(persistedData);
    if (userData.userId && typeof userData.userId === "string") {
      userData.userId = userData.userId.replace(/\\/g, "");
    }
    userId = userData?.userId;
  } catch (error) {
    console.error("Error parsing localStorage data:", error);
    userId = null;
  }

  const handleClick = () => {
    setSnackBarOpen(true);
  };
  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackBarOpen(false);
  };
  useEffect(() => {
    // Simulate URL search string
    if (idd.id) {
      setId(idd.id);
    } else {
      setId(null);
    }
  }, [idd.id]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();

      if (subject) query.append("subject", subject);
      if (level) query.append("level", level);
      if (searchLocation) query.append("location", searchLocation);
      if (paymentMethod.length > 0)
        query.append("paymentMethod", paymentMethod.join(","));

      const queryString = query.toString();
      const url = queryString 
        ? `${process.env.REACT_APP_SERVER}/api/courses?${queryString}`
        : `${process.env.REACT_APP_SERVER}/api/courses`;
        
      const response = await axios.get(url);
      setCourses(response.data);
    } catch (error) {
        setCourses([])
      console.error("Error fetching courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseModalOpen = (course) => {
    setSelectedCourse(course);
    navigate(`/course-detail/${course._id}`);
  };

  const navigteEdit = (course) => {
    setSelectedCourse(course);
    navigate(`/course/new/${course._id}`);
  };

  const handleCourseModalClose = () => {
    setCourseModalOpen(false);
    setSelectedCourse(null);
  };

  const handleMessageModalOpen = () => {
    setMessageModalOpen(true);
  };

  const handleMessageModalClose = () => {
    setMessageModalOpen(false);
  };

  const handleShareModalOpen = (course) => {
    setSelectedCourse(course);
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
  };

  const copyToClipboard = (course) => {
    const courseId = course._id;
    const url = `${process.env.REACT_APP_URL}/courses/${courseId}`;
    navigator.clipboard.writeText(url).then(
      function () {
        setSnackBarOpen(true);
      },
      function (err) {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const handleFacebookShare = (course) => {
    const courseId = course._id;
    const url = `${process.env.REACT_APP_URL}/courses/${courseId}`;
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  const handleWhatsAppShare = (course) => {
    const courseId = course._id;
    const url = `${process.env.REACT_APP_URL}/courses/${courseId}`;
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(url)}`,
      "_blank"
    );
  };

  useEffect(() => {
    fetchCourses();
  }, [subject, level, searchLocation, paymentMethod]);

  useEffect(() => {
    if (id) {
      fetchCourses();
    }
  }, [id, subject, level, searchLocation, paymentMethod]);

  useEffect(() => {
    if (id && courses.length > 0) {
      const course = courses.find((c) => c._id === id);
      if (course) {
        setSelectedCourse(course);
        setCourseModalOpen(true); // Open the modal for the course
      }
    }
  }, [courses, id]);

  const navigate = useNavigate();

  const pdfUrl = getCourseFileUrl(selectedCourse?.file);
  function onError(e) {
    // e.target.src = placeholder;
  }
  return (
    <div style={{ paddingTop: "20px", width: "100%", margin: "0px auto" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            marginTop: "20px",
          }}
        >
          <CircularProgress 
            sx={{ 
              color: "#712d93",
              marginBottom: "20px"
            }} 
          />
          <Typography
            variant="h6"
            sx={{
              color: "#666",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" }
            }}
          >
            Loading courses...
          </Typography>
        </Box>
      ) : courses.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            marginTop: "20px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#712d93",
              marginBottom: "20px",
              fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem" }
            }}
          >
            Sadly, no courses available!
          </Typography>
          <Curve style={{ maxWidth: "100%", marginTop: "20px" }} />
        </Box>
      ) : (
        <Box sx={{ 
          display: viewMode === 'grid' ? 'grid' : 'flex',
          gridTemplateColumns: viewMode === 'grid' ? { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } : undefined,
          flexDirection: viewMode === 'list' ? 'column' : undefined,
          gap: 3 
        }}>
          {courses.map((course, index) => (
            <Box
              key={index}
              onClick={() => viewMode === 'grid' && handleCourseModalOpen(course)}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                  transform: "translateY(-2px)",
                },
                cursor: viewMode === 'grid' ? 'pointer' : 'default',
                backgroundColor: "#fff",
                height: viewMode === 'grid' ? '460px' : { xs: 'auto', md: '220px' },
              }}
            >
              {/* Layout Container - Vertical for grid, Horizontal for list */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: viewMode === 'grid' ? 'column' : { xs: 'column', md: 'row' }, 
                height: '100%' 
              }}>
                {/* Course Image - Responsive based on view mode */}
                <Box
                  sx={{
                    width: viewMode === 'grid' ? '100%' : { xs: '100%', md: '240px' },
                    height: viewMode === 'grid' ? '200px' : { xs: '160px', md: '100%' },
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  {/* Gradient Overlay for better badge contrast */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 50%)',
                      pointerEvents: 'none',
                    }}
                  />
                  <img
                    src={getCourseImageUrl(course.image)}
                    alt={course.name || "Course image"}
                    onError={(e) => {
                      e.target.src = "/assets/course-place1.png";
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {/* Price Badge Overlay - Clean & Compact */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      backgroundColor: course?.coursePrice && course.coursePrice > 0 ? '#712d94' : '#10b981',
                      color: '#fff',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      letterSpacing: '0.3px',
                      backdropFilter: 'blur(8px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&::before': course?.coursePrice && course.coursePrice > 0 ? {
                        content: '"£"',
                        fontSize: '0.8rem',
                        opacity: 0.95,
                      } : {},
                    }}
                  >
                    {course?.coursePrice && course.coursePrice > 0
                      ? course.coursePrice
                      : "FREE"}
                  </Box>
                </Box>

                {/* Course Details - Right Side */}
                <Box sx={{ flex: 1, p: viewMode === 'grid' ? 2 : 2.5, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                  {/* Header Row: Title + View Details Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: viewMode === 'grid' ? 0.8 : 1.2, gap: 2 }}>
                    {/* Course Title */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        fontSize: viewMode === 'grid' ? { xs: '1rem', md: '1.1rem' } : { xs: '1.15rem', md: '1.25rem' },
                        color: '#1a1a1a',
                        lineHeight: 1.3,
                        flex: 1,
                      }}
                    >
                      {course.name}
                    </Typography>

                    {/* View Details Button (Right) - Hidden in Grid View */}
                    {viewMode === 'list' && (
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "#712d93",
                          color: "#fff",
                          padding: "8px 20px",
                          borderRadius: "24px",
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          boxShadow: '0 3px 10px rgba(113, 45, 147, 0.3)',
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: "#5a2470",
                            boxShadow: '0 5px 14px rgba(113, 45, 147, 0.4)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => handleCourseModalOpen(course)}
                      >
                        View Details →
                      </Button>
                    )}
                  </Box>

                  {/* Instructor Name OR Description (mutually exclusive to save space) */}
                  {course.by ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: viewMode === 'grid' ? 1.2 : 1.5 }}>
                      <FontAwesomeIcon 
                        icon={faChalkboardTeacher} 
                        style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.8rem' : '0.85rem' }} 
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: viewMode === 'grid' ? '0.8rem' : '0.85rem',
                          color: '#666',
                          fontWeight: 500,
                        }}
                      >
                        By <Box component="span" sx={{ color: '#712d93', fontWeight: 700 }}>{course.by}</Box>
                      </Typography>
                    </Box>
                  ) : course.description ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: viewMode === 'grid' ? '0.8rem' : '0.85rem',
                        color: '#555',
                        lineHeight: 1.4,
                        mb: viewMode === 'grid' ? 1.2 : 1,
                        display: '-webkit-box',
                        WebkitLineClamp: viewMode === 'grid' ? 2 : 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                      }}
                    >
                      {stripHtmlAndDecode(course.description)}
                    </Typography>
                  ) : null}

                  {/* Course Details Grid - Better Spacing */}
                  <Box sx={{ mb: viewMode === 'grid' ? 0.5 : 1, flex: 1 }}>
                    <Grid container spacing={viewMode === 'grid' ? 1 : 1}>
                      {course?.subject && (
                        <Grid item xs={12} sm={viewMode === 'grid' ? 12 : 6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <FontAwesomeIcon 
                              icon={faBook} 
                              style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.75rem' : '0.8rem', flexShrink: 0 }} 
                            />
                            <Tooltip 
                              title={course.subject} 
                              arrow
                              placement="top"
                              disableHoverListener={course.subject.length < 20}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: viewMode === 'grid' ? '0.85rem' : '0.90rem', 
                                  lineHeight: 1.4,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: course.subject.length >= 20 ? 'help' : 'default',
                                }}
                              >
                                <Box component="span" sx={{ color: '#888', fontWeight: 500, }}>Subject: </Box>
                                <Box component="span" sx={{ fontWeight: 700, color: '#1a1a1a', textTransform: 'capitalize' }}>
                                  {course.subject}
                                </Box>
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}

                      {course?.level && (
                        <Grid item xs={12} sm={viewMode === 'grid' ? 12 : 6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <FontAwesomeIcon 
                              icon={faGraduationCap} 
                              style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.75rem' : '0.8rem', flexShrink: 0 }} 
                            />
                            <Tooltip 
                              title={course.level} 
                              arrow
                              placement="top"
                              disableHoverListener={course.level.length < 20}
                            >
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: viewMode === 'grid' ? '0.85rem' : '0.90rem', 
                                  lineHeight: 1.4,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: course.level.length >= 20 ? 'help' : 'default',
                                }}
                              >
                                <Box component="span" sx={{ color: '#888', fontWeight: 500 }}>Level: </Box>
                                <Box component="span" sx={{ fontWeight: 700, color: '#1a1a1a', textTransform: 'capitalize' }}>
                                  {course.level}
                                </Box>
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}

                      {course?.types && (
                        <Grid item xs={12} sm={viewMode === 'grid' ? 12 : 6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <FontAwesomeIcon 
                              icon={faLocationDot} 
                              style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.75rem' : '0.8rem' }} 
                            />
                            <Typography variant="body2" sx={{ fontSize: viewMode === 'grid' ? '0.85rem' : '0.90rem', lineHeight: 1.4 }}>
                              <Box component="span" sx={{ color: '#888', fontWeight: 500, }}>Location: </Box>
                              <Box component="span" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                {course.types[0] &&
                                  course.types[0].charAt(0).toUpperCase() +
                                    course.types[0].slice(1)}
                                {course.types[0] && course.types[1] && " / "}
                                {course.types[1] &&
                                  course.types[1].charAt(0).toUpperCase() +
                                    course.types[1].slice(1)}
                              </Box>
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={viewMode === 'grid' ? 12 : 6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <FontAwesomeIcon 
                            icon={faCalendar} 
                            style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.75rem' : '0.8rem' }} 
                          />
                          <Typography variant="body2" sx={{ fontSize: viewMode === 'grid' ? '0.85rem' : '0.90rem', lineHeight: 1.4 }}>
                            <Box component="span" sx={{ color: '#888', fontWeight: 500 }}>Date: </Box>
                            <Box component="span" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                              {course?.isOngoing ? (
                                "Anytime"
                              ) : (
                                <>
                                  {new Date(course?.date[0]).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}{" "}
                                  -{" "}
                                  {new Date(course?.date[1]).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                  })}
                                </>
                              )}
                            </Box>
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Duration (if available) */}
                      {course?.duration && (
                        <Grid item xs={12} sm={viewMode === 'grid' ? 12 : 6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <FontAwesomeIcon 
                              icon={faClock} 
                              style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.75rem' : '0.8rem' }} 
                            />
                            <Typography variant="body2" sx={{ fontSize: viewMode === 'grid' ? '0.85rem' : '0.90rem', lineHeight: 1.4 }}>
                              <Box component="span" sx={{ color: '#888', fontWeight: 500 }}>Duration: </Box>
                              <Box component="span" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                {course.duration} {course.duration === '1' ? 'week' : 'weeks'}
                              </Box>
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {/* Time (if available) */}
                      {course?.time && formatTime(course.time) && (
                        <Grid item xs={12} sm={viewMode === 'grid' ? 12 : 6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <FontAwesomeIcon 
                              icon={faClock} 
                              style={{ color: '#712d93', fontSize: viewMode === 'grid' ? '0.75rem' : '0.8rem' }} 
                            />
                            <Typography variant="body2" sx={{ fontSize: viewMode === 'grid' ? '0.85rem' : '0.90rem', lineHeight: 1.4 }}>
                              <Box component="span" sx={{ color: '#888', fontWeight: 500 }}>Time: </Box>
                              <Box component="span" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                {formatTime(course.time)}
                              </Box>
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Edit Button (Only for course owner, at bottom) */}
                  {userId == course?.userId && (
                    <Box sx={{ mt: 'auto', pt: 1 }}>
                      <Button
                        variant="outlined"
                        sx={{
                          borderColor: "#712d93",
                          color: "#712d93",
                          padding: "8px 20px",
                          borderRadius: "24px",
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          border: '2px solid #712d93',
                          "&:hover": {
                            backgroundColor: "#712d93",
                            color: "#fff",
                            borderColor: "#712d93",
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(113, 45, 147, 0.3)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                        onClick={navigteEdit}
                      >
                        <FontAwesomeIcon
                          icon={faEdit}
                          style={{ marginRight: "6px", fontSize: '0.8rem' }}
                        />
                        Edit Course
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={handleClose}
        message="Link Copied to Clickboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <Modal
        open={courseModalOpen}
        onClose={handleCourseModalClose}
        aria-labelledby="course-modal-title"
        aria-describedby="course-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { sm: "80%", md: "70%", lg: "50%", xs: "90%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
            height: { sm: "80%", md: "55%", lg: "55%", xs: "90%" },
            overflowY: { sm: "auto", md: "auto", lg: "auto", xs: "auto" },
          }}
        >
          {selectedCourse && (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Typography id="course-modal-title" variant="h4" sx={{ mb: 2 }}>
                  {selectedCourse.name} - {selectedCourse.level} -{" "}
                  {selectedCourse.types}
                </Typography>
                <Typography
                  onClick={handleCourseModalClose}
                  sx={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "column",
                    md: "row",
                    lg: "row",
                  },
                }}
              >
                <Box>
                  <img
                    src={`${process.env.REACT_APP_SERVER}/${
                      selectedCourse.image ?? "default-image.jpg"
                    }`}
                    alt="courseimage"
                    style={{
                      height: "300px",
                      objectFit: "cover",
                      width: "300px",
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    id="course-modal-description"
                    variant="body1"
                    sx={{ mb: 2, fontSize: "1.4rem" }}
                  >
                    <FontAwesomeIcon
                      icon={faUser}
                      style={{ marginRight: "5px" }}
                    />{" "}
                    By {selectedCourse.by}
                  </Typography>
                  <Typography
                    id="course-modal-description"
                    variant="body1"
                    sx={{ mb: 2, fontSize: "1.4rem" }}
                  >
                    <FontAwesomeIcon
                      icon={faCalendar}
                      style={{ marginRight: "5px" }}
                    />{" "}
                    {selectedCourse.date}
                  </Typography>
                  <Typography
                    id="course-modal-description"
                    variant="body1"
                    sx={{ mb: 2, fontSize: "1.4rem" }}
                  >
                    <FontAwesomeIcon
                      icon={faMoneyBill}
                      style={{ marginRight: "5px" }}
                    />{" "}
                    {selectedCourse.coursePrice}
                  </Typography>
                  <Typography
                    id="course-modal-description"
                    variant="body1"
                    sx={{ mb: 2, fontSize: "1.4rem" }}
                  >
                    <FontAwesomeIcon
                      icon={faPhone}
                      style={{ marginRight: "5px" }}
                    />{" "}
                    {selectedCourse.telephone}
                  </Typography>
                  <Typography
                    id="course-modal-description"
                    variant="body1"
                    sx={{ mb: 2, fontSize: "1.4rem" }}
                  >
                    <FontAwesomeIcon
                      icon={faMapMarker}
                      style={{ marginRight: "5px" }}
                    />{" "}
                    {selectedCourse.location}
                  </Typography>
                  <Typography
                    id="course-modal-description"
                    variant="body1"
                    sx={{ mb: 2, fontSize: "1.4rem" }}
                  >
                    {selectedCourse.description}
                  </Typography>
                  <Box sx={{ display: "flex", gap: "10px" }}>
                    <Button
                      sx={{
                        backgroundColor: "#712d93",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "28px",
                        "&:hover": {
                          backgroundColor: "#609BDF",
                        },
                      }}
                      onClick={handleMessageModalOpen}
                    >
                      Contact Us
                    </Button>
                    <Button
                      sx={{
                        backgroundColor: "#712d93",
                        color: "#fff",
                        padding: "10px 20px",
                        borderRadius: "28px",
                        "&:hover": {
                          backgroundColor: "#609BDF",
                        },
                      }}
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download PDF
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <Modal
        open={messageModalOpen}
        onClose={handleMessageModalClose}
        aria-labelledby="message-modal-title"
        aria-describedby="message-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { sm: "80%", md: "50%", lg: "40%", xs: "90%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
            height: { sm: "80%", md: "70%", lg: "60%", xs: "90%" },
            overflowY: { sm: "auto", md: "auto", lg: "auto", xs: "auto" },
          }}
        >
          <Typography
            onClick={handleMessageModalClose}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "row-reverse",
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Typography>
          <Message />
        </Box>
      </Modal>

      <Modal
        open={shareModalOpen}
        onClose={handleShareModalClose}
        aria-labelledby="share-modal-title"
        aria-describedby="share-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "80%", md: "40%", lg: "30%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
            height: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selectedCourse && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  cursor: "pointer",
                  mb: 2,
                }}
                onClick={handleShareModalClose}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Box>
              <Typography
                id="share-modal-title"
                variant="h6"
                sx={{ mb: 2, fontWeight: "bold", fontSize: "2rem" }}
              >
                Share This Course
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Button
                  onClick={() => handleWhatsAppShare(selectedCourse)}
                  style={{
                    padding: 0,
                    fontSize: "2rem",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: "4rem" }} />
                </Button>

                <Button onClick={() => handleFacebookShare(selectedCourse)}>
                  <FacebookSharpIcon sx={{ fontSize: "4rem" }} />
                </Button>

                <Button onClick={() => copyToClipboard(selectedCourse)}>
                  <FontAwesomeIcon icon={faCopy} style={{ fontSize: "4rem" }} />
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};
